/**
 * BA tradie profile: flag files that need attention (expired or expiring soon).
 * Mirrors list UX thresholds where practical (see CompanyTradieList token/expiry tiers).
 */

// Company warning period for mandatory documents. Kept in sync with the
// backend "Needs Attention" rule (expiring within this many days). See
// docs/needs-attention-rule.md.
export const TRADIE_FILE_EXPIRING_SOON_DAYS = 30

export function normalizeFileStatus(status) {
  if (status == null || status === '') return ''
  return String(status)
    .toLowerCase()
    .replace(/'/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * File marked N/A for compliance — API may set `is_na` and/or literal status "N/A".
 * @param {Record<string, unknown>|null|undefined} file
 */
export function isTradieFileMarkedNa(file) {
  if (!file || typeof file !== 'object') return false
  if (file.is_na === true || file.is_na === 1) return true
  const st = normalizeFileStatus(file.status)
  return st === 'n/a' || st === 'na'
}

/**
 * Status cell for tradie file tables: never show literal "N/A" in Status — that
 * belongs in Reason. Preserves a real workflow status when the API still sends it.
 * @param {Record<string, unknown>|null|undefined} file
 */
export function formatTradieFileStatusColumnDisplay(file) {
  if (!file) return ''
  if (!isTradieFileMarkedNa(file)) {
    return formatTradieFilesStatusDisplay(file.status)
  }
  const st = normalizeFileStatus(file.status)
  if (st !== 'n/a' && st !== 'na' && file.status != null) {
    const raw = String(file.status).trim()
    if (raw !== '') return formatTradieFilesStatusDisplay(file.status)
  }
  return formatTradieFilesStatusDisplay('valid')
}

/**
 * Local midnight Date for expiry, or null. Handles YYYY-MM-DD prefix and AU DD/MM/YYYY
 * (avoids JS parsing 02/05/2026 as US Feb 5 vs 2 May).
 * @param {unknown} expiryRaw
 * @returns {Date|null}
 */
export function parseTradieFileExpiryLocalDate(expiryRaw) {
  if (expiryRaw == null) return null
  var s = String(expiryRaw).trim()
  if (s === '') return null

  var iso = /^(\d{4})-(\d{2})-(\d{2})/
  var isoMatch = iso.exec(s)
  if (isoMatch) {
    var y = parseInt(isoMatch[1], 10)
    var mo = parseInt(isoMatch[2], 10) - 1
    var da = parseInt(isoMatch[3], 10)
    var dtIso = new Date(y, mo, da)
    if (!isNaN(dtIso.getTime())) {
      dtIso.setHours(0, 0, 0, 0)
      return dtIso
    }
  }

  var slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
  var slashMatch = slash.exec(s)
  if (slashMatch) {
    var day = parseInt(slashMatch[1], 10)
    var month = parseInt(slashMatch[2], 10) - 1
    var year = parseInt(slashMatch[3], 10)
    var dtAu = new Date(year, month, day)
    if (!isNaN(dtAu.getTime())) {
      dtAu.setHours(0, 0, 0, 0)
      return dtAu
    }
  }

  var fallback = new Date(s)
  if (isNaN(fallback.getTime())) return null
  fallback.setHours(0, 0, 0, 0)
  return fallback
}

/**
 * @param {unknown} expiryRaw API date_expiry / ISO string / null
 * @returns {number|null} Whole days until expiry from local midnight (negative = past); null if unknown
 */
export function daysUntilFileExpiry(expiryRaw) {
  var expiry = parseTradieFileExpiryLocalDate(expiryRaw)
  if (!expiry) return null
  var today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
}

function expiryField(file) {
  if (!file || typeof file !== 'object') return null
  var candidates = [
    file.date_expiry,
    file.dateExpiry,
    file.expiry_date,
    file.expiryDate,
  ]
  for (var i = 0; i < candidates.length; i++) {
    var v = candidates[i]
    if (v != null && String(v).trim() !== '') return v
  }
  return null
}

/**
 * @param {Record<string, unknown>} file Tradie profile file row from API
 * @returns {'expired' | 'expiring_soon' | null}
 */
export function getTradieProfileFileAttentionLevel(file) {
  const status = normalizeFileStatus(file && file.status)
  const days = daysUntilFileExpiry(expiryField(file))

  const expiredByStatus = status === 'expired'

  const expiredByDate = days !== null && days < 0

  if (expiredByStatus || expiredByDate) {
    return 'expired'
  }

  const expiringSoonByStatus = status === 'expiring soon'

  const expiringSoonByDate =
    days !== null && days >= 0 && days <= TRADIE_FILE_EXPIRING_SOON_DAYS

  if (expiringSoonByStatus || expiringSoonByDate) {
    return 'expiring_soon'
  }

  return null
}

/**
 * "Set DD MMM YY" line for the Tradie Pass Verification tooltip. Returns ''
 * when the input is missing or unparseable, so callers can fall through cleanly.
 * @param {unknown} iso
 */
export function formatStatusSetDate(iso) {
  if (iso == null || iso === '') return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const day = String(d.getDate()).padStart(2, '0')
  const month = months[d.getMonth()]
  const year = String(d.getFullYear()).slice(-2)
  return `${day} ${month} ${year}`
}

/** Visible status text in tradie profile file tables */
export function formatTradieFilesStatusDisplay(value) {
  if (value === null || value === undefined || value === '') return value
  const normalized = normalizeFileStatus(value)
  if (
    normalized === 'cant be verified' ||
    normalized === 'cannot be verified'
  ) {
    return 'cannot be verified'
  }
  return value
}
